import { useState } from "react";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { gates } from "@excellent/ui/api/contracts/gates";
import type { GateDefWire, GateRecordStatus, ProjectGatesWire } from "@excellent/ui/api/contracts/gates";
import { CHART_TOOLTIP_STYLE } from "@excellent/ui/blocks/lens/chart-config";
import { activity } from "@excellent/ui/api/contracts/activity";
import { agentRuns } from "@excellent/ui/api/contracts/agent-runs";
import { agentSettings } from "@excellent/ui/api/contracts/agent-settings";
import { advisor } from "@excellent/ui/api/contracts/advisor";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { projectUniversalCompletionOverview } from "@excellent/core/verification-universal-surfaces-client";
import { resolveStoredVerdict } from "@excellent/core/verification-verdict-law";
import {
  verificationObservation,
  type ObservationDiscovery,
  type ObservationFirstUnknownReview,
  type ObservationReview,
} from "@excellent/ui/api/contracts/verification-observation";
import { AgentMark, AgentMarkCluster } from "./agent-mark";
import { D1LoopStatus } from "./d1-loop-status";
import { Icon } from "./icons";
import { verificationCanonicalClients } from "./api/canonical-clients";
import { useVerificationEnvOptional } from "./env-provider";
import { useWorkflowActions } from "./pages/workflows-actions";
import { useDurabilityAccountBoundary } from "./durability-account-boundary";

const HOME_ACTION = {
  openAgents: "home.open-agents",
  openContext: "home.open-context",
  openChanges: "home.open-changes",
  openInbox: "home.open-inbox",
  openControls: "home.open-controls",
  openReceipts: "home.open-receipts",
  openIncompleteReceipts: "home.open-incomplete-receipts",
  openRetrospectiveReceipts: "home.open-retrospective-receipts",
  defineFirstContract: "home.define-first-contract",
  openAgentActivity: "home.open-agent-activity",
  openActivity: "home.open-activity",
  openActivityGroup: "home.open-activity-group",
  retryAgentReadiness: "home.retry-agent-readiness",
  retryObservation: "home.retry-observation",
  closeAccessReview: "home.close-access-review",
  chooseAggregateSource: "home.choose-aggregate-source",
  durationOnce: "home.duration-once",
  durationSource: "home.duration-source",
  dismissAccessReview: "home.dismiss-access-review",
  manageSources: "home.manage-sources",
  chooseProject: "home.choose-project",
  resolveAccount: "home.resolve-account",
  reloadProposal: "home.reload-proposal",
  reviewChecks: "home.review-checks",
  openProofRecord: "home.open-proof-record",
  reconcileGuarantee: "home.reconcile-guarantee",
  retryGuaranteeLookup: "home.retry-guarantee-lookup",
  retryCheckLookup: "home.retry-check-lookup",
  openSavedGuarantee: "home.open-saved-guarantee",
  retryResultLookup: "home.retry-result-lookup",
  openLegacyCheckSetup: "home.open-legacy-check-setup",
  prepareNextRunCheck: "home.prepare-next-run-check",
} as const;

export function HomePage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const observation = useApi(verificationObservation.status, {});
  const agentReadiness = useApi(advisor.agentCliReadiness, {});
  const environmentContext = useVerificationEnvOptional();
  const environment = environmentContext?.environment ?? "real";
  const environmentReady = environmentContext ? environmentContext.ready : true;
  const workspaceOverview = useApi(
    verificationCanonicalClients.overview.operation,
    environmentReady ? { query: { env: environment } } : null,
  );
  const universalOverview = useApi(
    universalVerification.overview,
    environmentReady && environment === "real" ? {} : null,
  );
  const workerEnrollments = useApi(
    universalVerification.workerEnrollments,
    environmentReady && environment === "real" ? {} : null,
  );
  const workspaceActivity = useApi(
    activity.list,
    environmentReady ? { query: { env: environment } } : null,
  );
  const recentAgentRuns = useApi(
    agentRuns.feed,
    environmentReady && environment === "real" ? { query: { limit: 6 } } : null,
  );
  const accountBoundary = useDurabilityAccountBoundary();
  const [reviewing, setReviewing] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<"claude" | "codex" | null>(null);
  const [duration, setDuration] = useState<"once" | "source">("once");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [configuringAgentMcps, setConfiguringAgentMcps] = useState(false);
  const [agentConfigurationNotice, setAgentConfigurationNotice] = useState<string | null>(null);
  const [accessStoppedNotice, setAccessStoppedNotice] = useState<string | null>(null);
  const [discovery, setDiscovery] = useState<ObservationDiscovery | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedReviewRecord, setSelectedReviewRecord] = useState<ObservationReview | null>(null);
  const [uncertainReview, setUncertainReview] = useState<{
    discoveryId: string;
    candidateId: string;
  } | null>(null);

  const installed = agentReadiness.data?.agents.filter((agent) => agent.binary.installed) ?? [];
  const configured = agentReadiness.data?.agents.filter((agent) => agent.mcp.registered) ?? [];
  const configurableAgents = installed.filter(
    (agent) => agent.mcp.supported && !agent.mcp.registered,
  );
  const installedLabels = installed.map((agent) => agent.label);
  const configuredLabels = configured.map((agent) => agent.label);
  const detectedIds = new Set(installed.map((agent) => agent.id));
  const aggregateSources =
    observation.data?.sources.filter(
      (source) => detectedIds.has(source.source) && source.discovery === "aggregate",
    ) ?? [];
  const aggregateSource =
    aggregateSources.find((source) => source.source === selectedSourceId) ??
    aggregateSources[0] ??
    null;
  const aggregateSourcePath =
    aggregateSource?.source === "codex"
      ? "~/.codex/sessions/**/rollout-*.jsonl"
      : "~/.claude/projects";
  const isResolving =
    (observation.isLoading && !observation.data) ||
    (agentReadiness.isLoading && !agentReadiness.data);
  const restoredReview = !discovery
    ? ([...(observation.data?.reviewedCandidates ?? [])].sort(
        (a, b) => Date.parse(b.reviewedAt) - Date.parse(a.reviewedAt),
      )[0] ?? null)
    : null;
  const activeReview = selectedReviewRecord ?? restoredReview;
  const selectedCandidate =
    discovery?.candidates.find((candidate) => candidate.id === selectedCandidateId) ??
    (activeReview
      ? {
          id: activeReview.candidateId,
          label: activeReview.label,
          basis: `Reviewed from a bounded local aggregate scan at ${formatTime(activeReview.reviewedAt)}. The source and underlying aggregate counts were not retained in this review record.`,
          coverage: "aggregate" as const,
        }
      : null);
  const primaryCandidate = discovery?.candidates[0] ?? null;
  const otherCandidates = discovery?.candidates.slice(1) ?? [];
  const hasActiveProgress = reviewing || Boolean(discovery) || Boolean(selectedCandidate);
  const hasObservedSessionHistory = Boolean(discovery || activeReview);
  const canDiscoverAggregate = aggregateSource !== null;
  const observationReadinessNotice = observation.error
    ? "Observation status could not be read. No source is treated as connected or observable."
    : isResolving
      ? "Checking canonical CLI readiness. Observation grants and session records are not being read."
      : configured.length > 0
        ? "MCP registration is configured, but that is not live-session, Environment, Sandbox, execution, or verification proof. Restart or reload the agent before treating it as available."
        : installed.length > 0
          ? "An agent CLI is installed but not linked to Excellent MCP. Observation remains unavailable until setup is completed and the agent is restarted or reloaded."
          : "No installed agent CLI was detected by the canonical readiness check. Observation status remains not checked, not a negative proof from the observation endpoint.";
  const observationStatusEvidence =
    observation.data?.sources
      .map((source) =>
        [
          `${source.label}: presence=${source.presence}`,
          `installed=${source.installed === null ? "not_checked" : String(source.installed)}`,
          `linked=${source.linked === null ? "not_checked" : String(source.linked)}`,
          source.connectionFailure
            ? `failure=${source.connectionFailure.code}:${source.connectionFailure.retryable ? "retryable" : "terminal"}`
            : "failure=none",
        ].join(", "),
      )
      .join("; ") ?? null;
  const overviewPending =
    !environmentReady || (workspaceOverview.isLoading && !workspaceOverview.data);
  const universalPending =
    environment === "real" &&
    (!environmentReady || (universalOverview.isLoading && !universalOverview.data));
  const activityPending =
    !environmentReady || (workspaceActivity.isLoading && !workspaceActivity.data);
  const agentRunsPending =
    environment === "real" && recentAgentRuns.isLoading && !recentAgentRuns.data;
  const overviewData = workspaceOverview.data;
  const universalData = universalOverview.data;
  const completionProjection = universalData
    ? projectUniversalCompletionOverview(universalData)
    : null;
  const currentUniversalData = completionProjection?.current;
  const prospectiveReceipts =
    currentUniversalData?.receipts.filter(
      (receipt) => receipt.payload.assurance === "PROSPECTIVE",
    ) ?? [];
  const retrospectiveReceiptCount =
    currentUniversalData?.receipts.filter(
      (receipt) => receipt.payload.assurance === "RETROSPECTIVE",
    ).length ?? 0;
  const incompleteCompletionRecordCount = completionProjection
    ? completionProjection.incomplete.work.length +
      completionProjection.incomplete.contracts.length +
      completionProjection.incomplete.attempts.length +
      completionProjection.incomplete.receipts.length +
      completionProjection.incomplete.reviews.length
    : 0;
  const activityGroups = workspaceActivity.data?.groups ?? [];
  const activityEventCount = activityGroups.reduce((sum, group) => sum + group.count, 0);
  const agentRunRows = recentAgentRuns.data?.rows ?? [];
  const recentRunFailures = agentRunRows.filter(
    (run) =>
      run.status === "failed" || run.status === "budget_exceeded" || run.status === "aborted",
  ).length;
  const openWorkCount =
    currentUniversalData?.work.filter((work) => work.state !== "CLOSED").length ?? 0;
  const activeAttemptCount =
    currentUniversalData?.attempts.filter((attempt) => attempt.state === "OPEN").length ?? 0;
  const submittedAttemptCount =
    currentUniversalData?.attempts.filter((attempt) => attempt.state === "SUBMITTED").length ?? 0;
  const openReviewCount =
    currentUniversalData?.reviews.filter((review) => review.resolution === null).length ?? 0;
  const workAwaitingContract =
    currentUniversalData?.work.filter(
      (work) => work.state === "REQUESTED" || work.state === "CONTRACT_PENDING",
    ).length ?? 0;
  const obligationCount =
    currentUniversalData?.contracts.reduce(
      (sum, contract) => sum + contract.content.obligations.length,
      0,
    ) ?? 0;
  const verdictCounts = projectReceiptVerdictCounts(prospectiveReceipts);
  const nonPassingReceiptCount = countNonPassingReceipts(verdictCounts);
  const workspaceSummary =
    environment === "demo"
      ? overviewPending
        ? "Reading the Demo records available to this workspace."
        : workspaceOverview.error
          ? "The Demo verification summary is unavailable. Activity is shown independently below."
          : `${overviewData?.verifiedControlCount ?? 0} of ${overviewData?.controlCount ?? 0} Demo controls are verified.`
      : universalPending
        ? "Reading canonical work, contracts, attempts, reviews, and signed verdicts."
        : universalOverview.error
          ? "The canonical verification ledger is unavailable. Agent and activity feeds are shown independently below."
          : currentUniversalData?.work.length
            ? `${openWorkCount} current work item${openWorkCount === 1 ? " is" : "s are"} not closed, ${prospectiveReceipts.length} prospective signed verdict${prospectiveReceipts.length === 1 ? "" : "s"}, and ${openReviewCount} current review${openReviewCount === 1 ? "" : "s"} awaiting resolution.${retrospectiveReceiptCount > 0 ? ` ${retrospectiveReceiptCount} retrospective receipt${retrospectiveReceiptCount === 1 ? " is" : "s are"} recorded without completion authority.` : ""}${incompleteCompletionRecordCount > 0 ? ` ${incompleteCompletionRecordCount} historical lifecycle record${incompleteCompletionRecordCount === 1 ? " is" : "s are"} incomplete.` : ""}`
            : incompleteCompletionRecordCount > 0
              ? `No current work has entered the canonical lifecycle. ${incompleteCompletionRecordCount} historical lifecycle record${incompleteCompletionRecordCount === 1 ? " is" : "s are"} visible but incomplete.`
              : "No work has entered the canonical verification lifecycle yet.";

  const title = observation.error
    ? "Local observation is temporarily unavailable."
    : isResolving
      ? "Understanding what is already here."
      : selectedCandidate
        ? `${selectedCandidate.label} is saved as a candidate.`
        : primaryCandidate
          ? `I found a possible pattern: ${primaryCandidate.label}.`
          : configuredLabels.length > 0
            ? `Excellent MCP is configured for ${formatList(configuredLabels)}.`
            : installedLabels.length > 0
              ? `I found ${formatList(installedLabels)} on this computer.`
              : "Ready to understand this work.";

  const description = observation.error
    ? "Excellent could not confirm local tool presence. No successful observation, connection, write, or execution is shown."
    : isResolving
      ? "Checking local tool presence only. Registration files and session files are not being read."
      : selectedCandidate
        ? "A local aggregate signal supports the candidate, but does not prove it or identify a project on its own."
        : primaryCandidate
          ? `${primaryCandidate.basis} This is an aggregate candidate, not proof; prompts, responses, arguments, outputs, and file contents were not returned.`
          : configuredLabels.length > 0
            ? "Configuration is available to a new or restarted agent session. Excellent has not inferred a live session, execution context, Environment or Sandbox boundary, observation grant, or work occurrence from that config."
            : installedLabels.length > 0
              ? canDiscoverAggregate
                ? "Nothing was connected automatically. Review the exact local access before Excellent reads session event records or proposes a check."
                : "Only tool presence was checked. This installation does not expose an aggregate local scan yet; prompts, responses, files, and configuration were not read."
              : "Connect an agent, choose a source, or describe an outcome. Excellent will start with the smallest useful permission.";

  async function grantAndDiscoverNow() {
    if (!aggregateSource) return;
    setBusy(true);
    setActionError(null);
    setAccessStoppedNotice(null);
    try {
      const { grant } = await apiCall(verificationObservation.grant, {
        body: { source: aggregateSource.source, tier: "aggregate", duration, retentionDays: 0 },
      });
      const result = await apiCall(verificationObservation.discover, {
        body: { grantId: grant.id },
      });
      setDiscovery(result);
      setSelectedCandidateId(null);
      setSelectedReviewRecord(null);
      setUncertainReview(null);
      setReviewing(false);
    } catch (error) {
      const detail = errorMessage(error);
      setActionError(
        aggregateSource.connectionFailure
          ? `${aggregateSource.label} discovery failed: ${aggregateSource.connectionFailure.message}. No candidate or workflow was saved.`
          : `${detail} No candidate, workflow, live session, Environment, Sandbox, execution, or verification proof was saved for ${aggregateSource.label}.`,
      );
    } finally {
      await observation.mutate().catch(() => undefined);
      setBusy(false);
    }
  }

  async function configureDetectedAgentMcps() {
    setConfiguringAgentMcps(true);
    setAgentConfigurationNotice(null);
    let configuredNow = 0;
    let restartRequired = 0;
    const failed: string[] = [];
    try {
      for (const agent of configurableAgents) {
        try {
          const result = await apiCall(agentSettings.cliLink, { params: { id: agent.id } });
          if (!result.ok) {
            failed.push(agent.label);
            continue;
          }
          configuredNow += 1;
          if (result.restartRequired) restartRequired += 1;
        } catch {
          failed.push(agent.label);
        }
      }
      await agentReadiness.mutate().catch(() => undefined);
      if (failed.length > 0) {
        setAgentConfigurationNotice(
          `Configured ${configuredNow}; ${failed.join(", ")} still need manual setup in Coding Agent settings.`,
        );
      } else if (configuredNow > 0) {
        setAgentConfigurationNotice(
          restartRequired > 0
            ? `Configured ${configuredNow} detected agent${configuredNow === 1 ? "" : "s"}. Restart or reload ${
                configuredNow === 1 ? "it" : "them"
              }; the current session is still unverified.`
            : `Configured ${configuredNow} detected agent${configuredNow === 1 ? "" : "s"}.`,
        );
      }
    } finally {
      setConfiguringAgentMcps(false);
    }
  }

  function requestDiscovery() {
    if (duration === "source") {
      accountBoundary.requireAccount("background", grantAndDiscoverNow);
      return;
    }
    void grantAndDiscoverNow();
  }

  async function reviewCandidateNow(
    discoveryId: string,
    candidateId: string,
    reviewClaimToken: string,
  ) {
    setBusy(true);
    setActionError(null);
    setAccessStoppedNotice(null);
    try {
      const result = await apiCall(verificationObservation.review, {
        body: { discoveryId, candidateId, reviewClaimToken },
      });
      setSelectedCandidateId(candidateId);
      setSelectedReviewRecord(result.review);
      setUncertainReview(null);
      void observation.mutate().catch(() => undefined);
    } catch (error) {
      setUncertainReview({ discoveryId, candidateId });
      setActionError(
        `${errorMessage(error)} Excellent could not confirm whether the candidate review was saved. No workflow action is available until the review is reconciled.`,
      );
    } finally {
      setBusy(false);
    }
  }

  function requestCandidateReview(
    discoveryId: string,
    candidateId: string,
    reviewClaimToken: string,
  ) {
    accountBoundary.requireAccount("review", () =>
      reviewCandidateNow(discoveryId, candidateId, reviewClaimToken),
    );
  }

  async function reconcileCandidateReview() {
    if (!uncertainReview) return;
    setBusy(true);
    setActionError(null);
    try {
      const refreshed = await observation.mutate();
      const match = refreshed?.reviewedCandidates.find(
        (review) =>
          review.discoveryId === uncertainReview.discoveryId &&
          review.candidateId === uncertainReview.candidateId,
      );
      if (match) {
        setSelectedCandidateId(match.candidateId);
        setSelectedReviewRecord(match);
        setUncertainReview(null);
        return;
      }
      setUncertainReview(null);
      setActionError("No saved review was found. You can confirm the candidate review again.");
    } catch {
      setActionError(
        "Review status is unavailable. The review will not be offered again until lookup succeeds.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function revokeGrant(grantId: string) {
    setBusy(true);
    setActionError(null);
    try {
      await apiCall(verificationObservation.revoke, { body: { grantId } });
      await observation.mutate();
      setAccessStoppedNotice(
        "Access stopped. This grant is revoked, no longer appears active, and cannot be used for new local collection.",
      );
    } catch (error) {
      setActionError(`${errorMessage(error)} The local access grant could not be revoked.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vp-home-page" data-home-page="recognition" data-route="home">
      <header
        className="vp-home-hero"
        aria-busy={environment === "real" ? universalPending : overviewPending}
      >
        <div className="vp-home-eyebrow">
          <span className="vp-live-dot" aria-hidden="true" />
          {environment === "demo" ? "Demo environment" : "Real workspace"}
        </div>
        <h1>Home</h1>
        <p>{workspaceSummary}</p>
        <nav className="vp-home-summary-grid" aria-label="Workspace sections">
          <button type="button" data-action-id={HOME_ACTION.openAgents} onClick={() => onNavigate("/agents")}>
            <span className="vp-home-summary-heading">
              <AgentMarkCluster agentKeys={installed.map((agent) => agent.id)} size={15} />
              Agents
              <Icon name="arrow-right" size={13} />
            </span>
            <strong>
              {agentReadiness.error
                ? "Unavailable"
                : isResolving
                  ? "—"
                  : `${installed.length} detected`}
            </strong>
            <small>
              {agentReadiness.error || isResolving
                ? "Local tool status"
                : configured.length > 0
                  ? `${configured.length} configured · current session unverified`
                  : agentRunRows[0]
                    ? `Latest run ${agentRunRows[0].status}`
                    : "Excellent MCP not configured"}
            </small>
          </button>
          <button type="button" data-action-id={HOME_ACTION.openContext} onClick={() => onNavigate("/context")}>
            <span className="vp-home-summary-heading">
              <Icon name="book" size={15} />
              Context
              <Icon name="arrow-right" size={13} />
            </span>
            <strong>
              {environment === "demo"
                ? overviewPending
                  ? "—"
                  : `${overviewData?.controlCount ?? 0} controls`
                : universalOverview.error
                  ? "Unavailable"
                  : universalPending
                    ? "—"
                    : `${currentUniversalData?.contracts.length ?? 0} current contracts`}
            </strong>
            <small>
              {environment === "demo"
                ? "Demo control definitions"
                : universalOverview.error || universalPending
                  ? "Canonical contract status"
                  : `${obligationCount} current checks · ${currentUniversalData?.receipts.length ?? 0} current proof receipts`}
            </small>
          </button>
          <button type="button" data-action-id={HOME_ACTION.openChanges} onClick={() => onNavigate("/changes")}>
            <span className="vp-home-summary-heading">
              <Icon name="chart" size={15} />
              Changes
              <Icon name="arrow-right" size={13} />
            </span>
            <strong>
              {environment === "demo"
                ? activityPending
                  ? "—"
                  : `${activityEventCount} events`
                : universalOverview.error
                  ? "Unavailable"
                  : universalPending
                    ? "—"
                    : `${currentUniversalData?.work.length ?? 0} current work items`}
            </strong>
            <small>
              {environment === "demo"
                ? `${activityGroups.length} grouped updates`
                : universalOverview.error || universalPending
                  ? "Canonical work status"
                  : `${openWorkCount} not closed · ${openReviewCount} reviews`}
            </small>
          </button>
        </nav>
      </header>

      {environment === "real" && (
        <D1LoopStatus
          overview={completionProjection ?? undefined}
          pending={universalPending}
          unavailable={Boolean(universalOverview.error)}
          dogfoodFreshness={universalData?.dogfood.freshness}
          workerEnrollments={workerEnrollments.data}
          workersPending={!environmentReady || workerEnrollments.isLoading}
          workersUnavailable={Boolean(workerEnrollments.error)}
        />
      )}

      <section
        className="vp-home-section vp-home-verification"
        aria-labelledby="vp-home-verification-title"
      >
        <div className="vp-home-section-heading">
          <div>
            <span>Work verification</span>
            <h2 id="vp-home-verification-title">Contract to outcome</h2>
          </div>
          <button
            type="button"
            className="vp-home-inline-action"
            onClick={() =>
              void (environment === "real"
                ? universalOverview.mutate()
                : workspaceOverview.mutate())
            }
            disabled={environment === "real" ? universalPending : overviewPending}
          >
            <Icon name="refresh" size={13} />
            Refresh
          </button>
        </div>
        {environment === "demo" ? (
          <div className="vp-home-data-state" role="status">
            <Icon name="info" size={16} />
            <span>
              <strong>Canonical Work data is shown in Real.</strong>
              <small>
                Demo remains isolated and continues to use its scoped control projection.
              </small>
            </span>
          </div>
        ) : universalOverview.error ? (
          <div className="vp-home-data-state" role="status">
            <Icon name="alert" size={16} />
            <span>
              <strong>Canonical verification records are unavailable.</strong>
              <small>No Demo, cached, or legacy values are substituted here.</small>
            </span>
          </div>
        ) : universalPending ? (
          <div className="vp-home-data-state" role="status">
            <Icon name="refresh" size={16} />
            <span>
              <strong>Loading verification lifecycle</strong>
              <small>Reading work, contracts, attempts, receipts, reviews, and effects.</small>
            </span>
          </div>
        ) : (
          <>
            <dl className="vp-home-metrics">
              <div>
                <dt>Current work not closed</dt>
                <dd>
                  {openWorkCount}
                  <small>of {currentUniversalData?.work.length ?? 0}</small>
                </dd>
              </div>
              <div>
                <dt>Active runs</dt>
                <dd>
                  {activeAttemptCount}
                  <small>{submittedAttemptCount} submitted</small>
                </dd>
              </div>
              <div>
                <dt>Prospective signed verdicts</dt>
                <dd>
                  {prospectiveReceipts.length}
                  <small>{verdictCounts.PASS} pass</small>
                </dd>
              </div>
              <div>
                <dt>Open reviews</dt>
                <dd>{openReviewCount}</dd>
              </div>
            </dl>
            <VerificationCharts
              work={currentUniversalData?.work ?? []}
              verdictCounts={verdictCounts}
            />
            <div className="vp-home-provenance">
              <span>
                <strong>Source</strong> Canonical Work ledger
              </span>
              <span>
                <strong>Generated</strong>{" "}
                {universalData ? formatTime(universalData.generatedAt) : "Unavailable"}
              </span>
              <span data-freshness={universalData?.dogfood.freshness}>
                <strong>Protocol check</strong>{" "}
                {universalData?.dogfood.freshness.toLowerCase() ?? "unavailable"}
              </span>
            </div>
            <div className="vp-home-attention-list">
              {openReviewCount > 0 ? (
                <button type="button" data-action-id={HOME_ACTION.openInbox} onClick={() => onNavigate("/inbox")}>
                  <Icon name="inbox" size={15} />
                  <span>
                    <strong>
                      {openReviewCount} independent review{openReviewCount === 1 ? "" : "s"} waiting
                    </strong>
                    <small>Open Reviews to make or inspect the named decision.</small>
                  </span>
                  <Icon name="caret-right" size={9} />
                </button>
              ) : null}
              {workAwaitingContract > 0 ? (
                <button type="button" data-action-id={HOME_ACTION.openControls} onClick={() => onNavigate("/controls")}>
                  <Icon name="shield" size={15} />
                  <span>
                    <strong>
                      {workAwaitingContract} work item{workAwaitingContract === 1 ? "" : "s"}{" "}
                      awaiting a contract
                    </strong>
                    <small>
                      Review the frozen checks, evidence requirements, and acceptance authority.
                    </small>
                  </span>
                  <Icon name="caret-right" size={9} />
                </button>
              ) : null}
              {nonPassingReceiptCount > 0 ? (
                <button type="button" data-action-id={HOME_ACTION.openReceipts} onClick={() => onNavigate("/receipts")}>
                  <Icon name="flag" size={15} />
                  <span>
                    <strong>
                      {nonPassingReceiptCount} signed verdict
                      {nonPassingReceiptCount === 1 ? "" : "s"} did not pass
                    </strong>
                    <small>
                      Inspect the bound determinations, evidence, replay state, and signature.
                    </small>
                  </span>
                  <Icon name="caret-right" size={9} />
                </button>
              ) : null}
              {incompleteCompletionRecordCount > 0 ? (
                <button type="button" data-action-id={HOME_ACTION.openIncompleteReceipts} onClick={() => onNavigate("/receipts")}>
                  <Icon name="flag" size={15} />
                  <span>
                    <strong>
                      {incompleteCompletionRecordCount} historical lifecycle record
                      {incompleteCompletionRecordCount === 1 ? " is" : "s are"} incomplete
                    </strong>
                    <small>
                      Recorded states remain inspectable, but they do not count as current assurance
                      or authorize an action.
                    </small>
                  </span>
                  <Icon name="caret-right" size={9} />
                </button>
              ) : null}
              {retrospectiveReceiptCount > 0 ? (
                <button type="button" data-action-id={HOME_ACTION.openRetrospectiveReceipts} onClick={() => onNavigate("/receipts")}>
                  <Icon name="flag" size={15} />
                  <span>
                    <strong>
                      {retrospectiveReceiptCount} retrospective receipt
                      {retrospectiveReceiptCount === 1 ? " is" : "s are"} recorded
                    </strong>
                    <small>
                      Recorded verdict facts remain inspectable, but cannot count as prospective
                      assurance or authorize an action.
                    </small>
                  </span>
                  <Icon name="caret-right" size={9} />
                </button>
              ) : null}
              {(currentUniversalData?.work.length ?? 0) === 0 ? (
                <button type="button" data-action-id={HOME_ACTION.defineFirstContract} onClick={() => onNavigate("/context")}>
                  <Icon name="plus" size={15} />
                  <span>
                    <strong>Define the first work contract</strong>
                    <small>
                      Start from requester intent, policy, or a reviewed contract proposal.
                    </small>
                  </span>
                  <Icon name="caret-right" size={9} />
                </button>
              ) : openReviewCount === 0 &&
                workAwaitingContract === 0 &&
                nonPassingReceiptCount === 0 &&
                retrospectiveReceiptCount === 0 &&
                incompleteCompletionRecordCount === 0 ? (
                <div className="vp-home-data-state">
                  <Icon name="check" size={16} />
                  <span>
                    <strong>No canonical decision work is waiting</strong>
                    <small>
                      Visible contracts, receipts, and reviews have no unresolved exception.
                    </small>
                  </span>
                </div>
              ) : null}
            </div>
          </>
        )}
      </section>

      <div className="vp-home-feed-grid">
        <section className="vp-home-section" aria-labelledby="vp-home-agent-runs-title">
          <div className="vp-home-section-heading">
            <div>
              <span>Agents</span>
              <h2 id="vp-home-agent-runs-title">Latest runs</h2>
            </div>
            <button
              type="button"
              className="vp-home-inline-action"
              data-action-id={HOME_ACTION.openAgentActivity}
              onClick={() => onNavigate("/activity?view=agents")}
            >
              All agent activity
              <Icon name="arrow-right" size={12} />
            </button>
          </div>
          {environment === "demo" ? (
            <p className="vp-home-empty">
              Live worker runs are kept out of Demo. Switch to Real to see this node's agent
              history.
            </p>
          ) : recentAgentRuns.error ? (
            <div className="vp-home-data-state">
              <Icon name="alert" size={16} />
              <span>
                <strong>Agent runs are unavailable.</strong>
                <small>Use refresh to try the live feed again.</small>
              </span>
            </div>
          ) : agentRunsPending ? (
            <div className="vp-home-data-state">
              <Icon name="refresh" size={16} />
              <span>
                <strong>Loading agent runs</strong>
                <small>Reading the latest live worker history.</small>
              </span>
            </div>
          ) : agentRunRows.length === 0 ? (
            <p className="vp-home-empty">No live agent runs have been recorded.</p>
          ) : (
            <div className="vp-home-feed-list">
              {agentRunRows.slice(0, 5).map((run) => (
                <button
                  type="button"
                  key={run.id}
                  data-action-id={HOME_ACTION.openAgentActivity}
                  onClick={() => onNavigate("/activity?view=agents")}
                >
                  <AgentMark agentKey={run.agent_id} size={19} />
                  <span>
                    <strong>
                      {run.role} · {run.agent_id}
                    </strong>
                    <small>
                      {run.goal_title ?? run.model ?? `Run ${run.id}`} ·{" "}
                      {formatTime(run.started_at)}
                    </small>
                  </span>
                  <em data-status={run.status}>{run.status.replaceAll("_", " ")}</em>
                </button>
              ))}
              {recentRunFailures > 0 ? (
                <p className="vp-home-feed-note">
                  {recentRunFailures} of the latest {agentRunRows.length} runs need attention.
                </p>
              ) : null}
            </div>
          )}
        </section>

        <section className="vp-home-section" aria-labelledby="vp-home-activity-title">
          <div className="vp-home-section-heading">
            <div>
              <span>Changes</span>
              <h2 id="vp-home-activity-title">Recent activity</h2>
            </div>
            <button
              type="button"
              className="vp-home-inline-action"
              data-action-id={HOME_ACTION.openActivity}
              onClick={() => onNavigate("/activity")}
            >
              Open activity
              <Icon name="arrow-right" size={12} />
            </button>
          </div>
          {workspaceActivity.error ? (
            <div className="vp-home-data-state">
              <Icon name="alert" size={16} />
              <span>
                <strong>Activity is unavailable.</strong>
                <small>No cached or sample events are substituted.</small>
              </span>
            </div>
          ) : activityPending ? (
            <div className="vp-home-data-state">
              <Icon name="refresh" size={16} />
              <span>
                <strong>Loading activity</strong>
                <small>Reading grouped workspace changes.</small>
              </span>
            </div>
          ) : workspaceActivity.data?.scope?.available === false ? (
            <p className="vp-home-empty">Activity is not recorded for this environment.</p>
          ) : activityGroups.length === 0 ? (
            <p className="vp-home-empty">No workspace activity has been recorded.</p>
          ) : (
            <div className="vp-home-feed-list">
              {activityGroups.slice(0, 5).map((group) => (
                <button
                  type="button"
                  key={group.key}
                  data-action-id={HOME_ACTION.openActivityGroup}
                  onClick={() => onNavigate(group.href.startsWith("/") ? group.href : "/activity")}
                >
                  <span className="vp-home-feed-icon">
                    <Icon name="chart" size={13} />
                  </span>
                  <span>
                    <strong>{group.label}</strong>
                    <small>{group.members[0]?.label ?? formatTime(group.latest)}</small>
                  </span>
                  <em>{group.count}</em>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="vp-home-section vp-home-node" aria-labelledby="vp-home-recognition-title">
        <div className="vp-home-section-heading">
          <div>
            <span>Local agent context</span>
            <h2 id="vp-home-recognition-title">{title}</h2>
          </div>
          <small>This node</small>
        </div>
        <section
          className="vp-home-recognition"
          aria-labelledby="vp-home-recognition-title"
          data-companion-recognition="agent-presence"
        >
          <p>{description}</p>
          <p className="vp-observation-note">Session history requires a separate grant</p>
          {configurableAgents.length > 0 ||
          agentReadiness.error ||
          !hasActiveProgress ||
          isResolving ||
          observation.error ? (
            <div className="vp-home-actions">
              {agentReadiness.isLoading && !agentReadiness.data ? (
                <button type="button" className="vp-home-primary" disabled>
                  <Icon name="refresh" size={15} />
                  Checking coding agents
                </button>
              ) : configurableAgents.length > 0 ? (
                <button
                  type="button"
                  className="vp-home-primary"
                  data-action-id="home.configure-detected-agents"
                  disabled={configuringAgentMcps}
                  onClick={() => void configureDetectedAgentMcps()}
                >
                  <Icon name={configuringAgentMcps ? "refresh" : "plug"} size={15} />
                  {configuringAgentMcps
                    ? "Configuring Excellent MCP"
                    : `Configure ${configurableAgents.length} detected agent${
                        configurableAgents.length === 1 ? "" : "s"
                      }`}
                </button>
              ) : agentReadiness.error ? (
                <button
                  type="button"
                  className="vp-home-primary"
                  data-action-id={HOME_ACTION.retryAgentReadiness}
                  onClick={() => void agentReadiness.mutate()}
                >
                  <Icon name="refresh" size={15} />
                  Retry coding-agent check
                </button>
              ) : observation.isLoading && !observation.data ? (
                <button type="button" className="vp-home-primary" disabled>
                  <Icon name="refresh" size={15} />
                  Checking this node
                </button>
              ) : observation.error ? (
                <button
                  type="button"
                  className="vp-home-primary"
                  data-action-id={HOME_ACTION.retryObservation}
                  onClick={() => void observation.mutate()}
                >
                  <Icon name="refresh" size={15} />
                  Retry local check
                </button>
              ) : canDiscoverAggregate ? (
                <button
                  type="button"
                  className="vp-home-primary"
                  data-action-id="home.review-agent-access"
                  onClick={() => setReviewing(true)}
                >
                  <Icon name="eye" size={15} />
                  Review bounded {aggregateSources.length > 1
                    ? "agent"
                    : aggregateSource.label}{" "}
                  scan
                </button>
              ) : (
                <button
                  type="button"
                  className="vp-home-primary"
                  data-action-id="home.manage-agent-sources"
                  onClick={() => onNavigate("/settings/agents/clis")}
                >
                  <Icon name="plug" size={15} />
                  {installed.length > 0 ? "Manage sources" : "Connect one source"}
                </button>
              )}
            </div>
          ) : null}
          {agentConfigurationNotice ? (
            <p className="vp-observation-note" role="status">
              {agentConfigurationNotice}
            </p>
          ) : null}
          <p
            className="vp-observation-note"
            role="status"
            data-observation-readiness-notice="true"
          >
            {observationReadinessNotice}
          </p>
          {observationStatusEvidence ? (
            <p className="vp-observation-note" data-observation-status-evidence="true">
              {observationStatusEvidence}
            </p>
          ) : null}
          {accessStoppedNotice ? (
            <p className="vp-observation-note" role="status" data-observation-access-stopped="true">
              {accessStoppedNotice}
            </p>
          ) : null}
        </section>
        <dl className="vp-home-basis vp-home-basis-compact">
          <div>
            <dt>{observation.error || isResolving ? "Status" : "Observed"}</dt>
            <dd>
              {observation.error
                ? "Unavailable"
                : isResolving
                  ? "Checking presence"
                  : installed.length > 0
                    ? `${installed.length} installed tool${installed.length === 1 ? "" : "s"}`
                    : "No installed tools"}
            </dd>
          </div>
          <div>
            <dt>Scope</dt>
            <dd>This node</dd>
          </div>
          {!observation.error ? (
            <>
              <div>
                <dt>{hasObservedSessionHistory ? "Not returned" : "Not read"}</dt>
                <dd>
                  {hasObservedSessionHistory
                    ? "Prompts, responses, arguments, outputs, and file contents"
                    : "Prompts, responses, and session files"}
                </dd>
              </div>
              <div>
                <dt>Egress</dt>
                <dd>None</dd>
              </div>
            </>
          ) : null}
        </dl>
      </section>

      {!observation.error && reviewing ? (
        <section className="vp-observation-review" aria-labelledby="observation-review-title">
          <div className="vp-home-section-heading">
            <div>
              <span>Local permission</span>
              <h2 id="observation-review-title">
                Let Excellent recognize aggregate {aggregateSource?.label ?? "agent"} patterns?
              </h2>
            </div>
            <button
              type="button"
              className="vp-icon-command"
              data-action-id={HOME_ACTION.closeAccessReview}
              aria-label="Close access review"
              onClick={() => setReviewing(false)}
            >
              <Icon name="x" size={14} />
            </button>
          </div>
          <dl className="vp-observation-scope">
            <div>
              <dt>Source</dt>
              <dd>
                {aggregateSource?.label ?? "Local agent"} session records in{" "}
                <code>{aggregateSourcePath}</code>
              </dd>
            </div>
            <div>
              <dt>Reads locally</dt>
              <dd>
                {aggregateSource?.detail ??
                  "Up to 2 MB from each of 40 recent session records, parsed to return aggregate tool and category counts."}
              </dd>
            </div>
            <div>
              <dt>Does not return</dt>
              <dd>Prompts, responses, tool arguments, outputs, or file contents</dd>
            </div>
            <div>
              <dt>Processing</dt>
              <dd>On this node only</dd>
            </div>
            <div>
              <dt>Egress</dt>
              <dd>None</dd>
            </div>
          </dl>
          <div className="vp-observation-controls">
            {aggregateSources.length > 1 ? (
              <fieldset>
                <legend>Source</legend>
                <div className="vp-segmented">
                  {aggregateSources.map((source) => (
                    <button
                      type="button"
                      key={source.source}
                      data-action-id={HOME_ACTION.chooseAggregateSource}
                      data-active={aggregateSource?.source === source.source}
                      onClick={() => setSelectedSourceId(source.source as "claude" | "codex")}
                    >
                      {source.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            ) : null}
            <fieldset>
              <legend>Duration</legend>
              <div className="vp-segmented">
                <button
                  type="button"
                  data-action-id={HOME_ACTION.durationOnce}
                  data-active={duration === "once"}
                  onClick={() => setDuration("once")}
                >
                  One scan
                </button>
                <button
                  type="button"
                  data-action-id={HOME_ACTION.durationSource}
                  data-active={duration === "source"}
                  onClick={() => setDuration("source")}
                >
                  30 days
                </button>
              </div>
            </fieldset>
            <div className="vp-observation-retention">
              <span>Result retention</span>
              <strong>Browser session</strong>
              <small>
                The grant and consumption record remain; aggregate counts are not written to the
                database.
              </small>
            </div>
          </div>
          {!aggregateSource ? (
            <p className="vp-observation-note">
              No installed source with aggregate local discovery was detected on this node.
            </p>
          ) : null}
          {actionError ? (
            <p className="vp-observation-error" role="alert">
              {actionError}
            </p>
          ) : null}
          <div className="vp-home-actions">
            <button
              type="button"
              className="vp-home-primary"
              disabled={busy || !aggregateSource}
              onClick={requestDiscovery}
            >
              <Icon name={busy ? "refresh" : "eye"} size={15} />
              {busy ? "Reading locally" : "Allow and discover"}
            </button>
            <button type="button" data-action-id={HOME_ACTION.dismissAccessReview} onClick={() => setReviewing(false)}>
              Not now
            </button>
          </div>
          {observation.data?.activeGrants.map((grant) => (
            <div className="vp-active-grant" key={grant.id}>
              <span>
                <strong>{grant.source}</strong>
                <small>{grant.duration === "once" ? "One scan" : "Until expiry"} · no egress</small>
              </span>
              <button type="button" disabled={busy} onClick={() => void revokeGrant(grant.id)}>
                Revoke
              </button>
            </div>
          ))}
        </section>
      ) : null}

      {observation.error || selectedCandidate ? null : discovery ? (
        <section
          className="vp-home-section vp-discovery-result"
          aria-labelledby="discovery-result-title"
        >
          <div className="vp-home-section-heading">
            <div>
              <span>Discovered locally</span>
              <h2 id="discovery-result-title">
                {primaryCandidate ? primaryCandidate.label : "No supported workflow pattern yet"}
              </h2>
            </div>
            <small>
              {discovery.aggregate.sessionCount} sessions · {discovery.aggregate.totalToolCalls}{" "}
              tool events
            </small>
          </div>
          {primaryCandidate ? (
            <div className="vp-discovery-row" key={primaryCandidate.id}>
              <Icon name="seal" size={15} />
              <span>
                <strong>Aggregate candidate, not proof</strong>
                <small>{primaryCandidate.basis}</small>
              </span>
              {uncertainReview ? null : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    requestCandidateReview(
                      discovery.discoveryId,
                      primaryCandidate.id,
                      discovery.reviewClaimToken,
                    )
                  }
                >
                  {busy ? "Saving review" : "Save review and find checks"}
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="vp-home-empty">
                No multi-session aggregate pattern met the proposal threshold. Nothing was created.
              </p>
              <div className="vp-home-actions">
                <button type="button" data-action-id={HOME_ACTION.manageSources} onClick={() => onNavigate("/settings/agents/clis")}>
                  Manage sources
                </button>
              </div>
            </>
          )}
          {otherCandidates.length > 0 && !uncertainReview ? (
            <details className="vp-home-more">
              <summary>
                {otherCandidates.length} other possible pattern
                {otherCandidates.length === 1 ? "" : "s"}
              </summary>
              {otherCandidates.map((candidate) => (
                <div className="vp-discovery-row" key={candidate.id}>
                  <Icon name="seal" size={15} />
                  <span>
                    <strong>{candidate.label}</strong>
                    <small>{candidate.basis}</small>
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      requestCandidateReview(
                        discovery.discoveryId,
                        candidate.id,
                        discovery.reviewClaimToken,
                      )
                    }
                  >
                    Save review and find checks
                  </button>
                </div>
              ))}
            </details>
          ) : null}
          <p className="vp-observation-note">
            {discovery.sessionFilesRead
              ? "Session records were parsed locally. Prompt and response text was not returned or sent elsewhere."
              : `No readable ${aggregateSource?.label ?? "agent"} session records were found. Nothing was inferred.`}
          </p>
          {actionError ? (
            <p className="vp-observation-error" role="alert">
              {actionError}
            </p>
          ) : null}
          {uncertainReview ? (
            <div className="vp-home-actions">
              <button
                type="button"
                className="vp-home-primary"
                disabled={busy}
                onClick={() => void reconcileCandidateReview()}
              >
                {busy ? "Checking review" : "Reconcile saved review"}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {!observation.error && selectedCandidate && activeReview ? (
        <CandidateCheckProgress
          candidate={selectedCandidate}
          review={activeReview}
          onNavigate={onNavigate}
          requireDurableAction={(run) => {
            accountBoundary.requireAccount("workflow", run);
          }}
          accountReady={accountBoundary.ready}
          accountUnavailable={Boolean(accountBoundary.error)}
          sourceLabel={
            selectedReviewRecord && discovery
              ? (observation.data?.sources.find((source) => source.source === discovery.source)
                  ?.label ?? discovery.source)
              : null
          }
        />
      ) : null}

      <section className="vp-home-section vp-home-compliance" aria-labelledby="compliance-lens">
        <div>
          <span>Compliance lens</span>
          <h2 id="compliance-lens">The same evidence can support SOC 2.</h2>
          <p>
            {workspaceOverview.error
              ? "The operational control projection is unavailable."
              : overviewPending
                ? "Reading the workspace's operational control projection."
                : `${overviewData?.verifiedControlCount ?? 0} of ${overviewData?.controlCount ?? 0} controls are verified, with ${overviewData?.openFindingCount ?? 0} open finding${overviewData?.openFindingCount === 1 ? "" : "s"}. Controls organize observed evidence, gaps, decisions, and receipt metadata for SOC 2 preparation. Signed, independently verifiable receipts are not yet available.`}
          </p>
        </div>
        <div className="vp-home-text-actions">
          <button
            type="button"
            data-action-id="home.open-controls"
            onClick={() => onNavigate("/controls")}
          >
            Open contracts
          </button>
          <button
            type="button"
            data-action-id="home.open-evidence-packs"
            onClick={() => onNavigate("/packs")}
          >
            Evidence packs
          </button>
        </div>
      </section>
      {!observation.error ? accountBoundary.checkpoint : null}
    </div>
  );
}

type WorkState =
  | "REQUESTED"
  | "CONTRACT_PENDING"
  | "OPEN"
  | "IN_PROGRESS"
  | "SUBMITTED"
  | "VERIFYING"
  | "DETERMINED"
  | "ENFORCED"
  | "REVIEWING"
  | "CLOSED";

// The chart iterates `Object.keys(VERDICT_COLORS)`, so a status absent here is
// absent from the chart entirely — not mis-coloured, invisible.
const VERDICT_COLORS = {
  PASS: "#2f8b5b",
  MISMATCH: "#8c2f52",
  FAIL: "#b34a4a",
  UNKNOWN: "#a1762b",
  NOT_RUN: "#89948d",
  // Blue: the one verdict that is neither good, bad, nor missing. Sharing
  // NOT_RUN's grey would make a check that is running look like one that never
  // started, and the chart iterates these keys, so an omission is invisibility.
  PENDING: "#3f6fa8",
} as const;

function VerificationCharts({
  work,
  verdictCounts,
}: {
  work: readonly { state: WorkState }[];
  verdictCounts: Record<keyof typeof VERDICT_COLORS, number>;
}) {
  const countWork = (...states: WorkState[]) =>
    work.filter((item) => states.includes(item.state)).length;
  const workData = [
    { label: "Requested", value: countWork("REQUESTED", "CONTRACT_PENDING", "OPEN") },
    { label: "Working", value: countWork("IN_PROGRESS") },
    { label: "Submitted", value: countWork("SUBMITTED", "VERIFYING") },
    { label: "Determined", value: countWork("DETERMINED", "ENFORCED") },
    { label: "Reviewing", value: countWork("REVIEWING") },
    { label: "Closed", value: countWork("CLOSED") },
  ];
  const verdictData = (Object.keys(VERDICT_COLORS) as (keyof typeof VERDICT_COLORS)[]).map(
    (status) => ({
      label: status === "NOT_RUN" ? "Not run" : status[0] + status.slice(1).toLowerCase(),
      status,
      value: verdictCounts[status],
    }),
  );

  return (
    <div className="vp-home-chart-grid" aria-label="Verification distributions">
      <figure>
        <figcaption>
          <strong>Work lifecycle</strong>
          <small>Completion-aware work items by stage</small>
        </figcaption>
        {work.length > 0 ? (
          <ResponsiveContainer width="100%" height={190} minWidth={0}>
            <BarChart
              data={workData}
              layout="vertical"
              margin={{ top: 4, right: 28, bottom: 4, left: 4 }}
              accessibilityLayer
            >
              <CartesianGrid stroke="#e5eae6" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#748078", fontSize: "var(--type-subtext-size)" }}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={72}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#536158", fontSize: "var(--type-subtext-size)" }}
              />
              <Tooltip
                cursor={{ fill: "#f1f5f2" }}
                contentStyle={{
                  ...CHART_TOOLTIP_STYLE,
                  boxShadow: "none",
                  fontSize: "var(--type-subtext-size)",
                }}
              />
              <Bar
                dataKey="value"
                name="Work items"
                fill="#4f7f64"
                radius={[0, 3, 3, 0]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="value"
                  position="right"
                  fill="#536158"
                  fontSize="var(--type-subtext-size)"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p>No canonical work records.</p>
        )}
        <small className="vp-home-chart-note">
          Requested groups requested, contract-pending, and open work. Submitted includes
          verification in progress. Determined includes enforced work.
        </small>
      </figure>
      <figure>
        <figcaption>
          <strong>Signed verdicts</strong>
          <small>Completion-aware receipt aggregate status</small>
        </figcaption>
        {Object.values(verdictCounts).some((count) => count > 0) ? (
          <ResponsiveContainer width="100%" height={190} minWidth={0}>
            <BarChart
              data={verdictData}
              margin={{ top: 8, right: 8, bottom: 0, left: -22 }}
              accessibilityLayer
            >
              <CartesianGrid stroke="#e5eae6" vertical={false} />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#536158", fontSize: "var(--type-subtext-size)" }}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#748078", fontSize: "var(--type-subtext-size)" }}
              />
              <Tooltip
                cursor={{ fill: "#f1f5f2" }}
                contentStyle={{
                  ...CHART_TOOLTIP_STYLE,
                  boxShadow: "none",
                  fontSize: "var(--type-subtext-size)",
                }}
              />
              <Bar dataKey="value" name="Receipts" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {verdictData.map((entry) => (
                  <Cell key={entry.status} fill={VERDICT_COLORS[entry.status]} />
                ))}
                <LabelList
                  dataKey="value"
                  position="top"
                  fill="#536158"
                  fontSize="var(--type-subtext-size)"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p>No signed verdict receipts.</p>
        )}
        <small className="vp-home-chart-note">
          Historical receipts without canonical completion summaries stay visible elsewhere as
          incomplete and never enter these counts.
        </small>
      </figure>
    </div>
  );
}

type ObservationCandidate = ObservationDiscovery["candidates"][number];

/**
 * The outcome of the one named check this surface runs.
 *
 * `counts` are a real per-gate tally and are shown as one. `verdict` is set instead when
 * the run returned a durable result record without a tally: then the record's verdict is
 * the only outcome there is, and inventing a tally from it is what rendered MISMATCH and
 * UNKNOWN as "0 passing and 0 failing gates" — a check that found a contradiction, and a
 * check the verifier could not decide, both displayed as a check that found nothing.
 * `verdict: null` means the record carried no verdict at all, which is its own fact.
 */
type CheckResult = {
  label: string;
  counts: { passed: number; failed: number } | null;
  verdict: GateRecordStatus | null | undefined;
};

/**
 * One sentence per outcome. Every verdict the contract declares has an entry, so a verdict
 * can never fall through to silence: `satisfies Record<GateRecordStatus, string>` turns a
 * sixth verdict into a COMPILE error rather than a blank.
 *
 * PASS and FAIL keep the exact wording and the exact synthesized tally they rendered
 * before, so the ordinary case is byte-identical. The two conclusive-but-not-PASS verdicts
 * differ in WHICH finding was reached, and the two no-finding verdicts reached none at
 * all — so neither of those may be reported as a gate tally.
 */
const VERDICT_SENTENCE = {
  PASS: "recorded a PASS verdict",
  MISMATCH:
    "recorded a MISMATCH verdict: an independent record was consulted and it contradicts this claim. That is a finding against the work, not a missing result",
  FAIL: "recorded a FAIL verdict",
  UNKNOWN:
    "recorded an UNKNOWN verdict: the verifier could not tell, so nothing is counted as passing or failing. That is a gap in the check, not a result for the work",
  NOT_RUN: "recorded a NOT_RUN verdict: no check was executed, so there is nothing to count",
  PENDING:
    "recorded a PENDING verdict: the check is not yet due, so there is nothing to count yet. That is a deadline that has not arrived, not a gap and not a result",
} satisfies Record<GateRecordStatus, string>;

/** The tally PASS and FAIL have always synthesized for a single named check. The other
 *  verdicts get none: there is no honest passing/failing count for "could not tell". */
const VERDICT_COUNTS: Partial<Record<GateRecordStatus, { passed: number; failed: number }>> = {
  PASS: { passed: 1, failed: 0 },
  FAIL: { passed: 0, failed: 1 },
};

export function projectCheckResult(label: string, verdict: GateRecordStatus | undefined): CheckResult {
  return { label, counts: (verdict && VERDICT_COUNTS[verdict]) ?? null, verdict: verdict ?? null };
}

/**
 * The receipt buckets this screen counts, read AT `now`.
 *
 * A stored receipt is signed and append-only, so a PENDING one keeps saying
 * PENDING long after its deadline; `resolveStoredVerdict` is the one engine
 * that says what it means today, and this surface holds no deadline
 * arithmetic of its own. `now` is a parameter with an edge default for the
 * same reason the other two readers have one: a render is the edge, and a test
 * must be able to stand on either side of a deadline without stubbing a clock.
 *
 * Extracted from the page body unchanged in every other respect — with no
 * PENDING receipt in the set, it returns exactly the counts the inline filters
 * returned.
 */
export function projectReceiptVerdictCounts(
  receipts: readonly { payload: { aggregateStatus: GateRecordStatus } }[],
  now: string = new Date().toISOString(),
): Record<GateRecordStatus, number> {
  const counts: Record<GateRecordStatus, number> = {
    PASS: 0,
    MISMATCH: 0,
    FAIL: 0,
    UNKNOWN: 0,
    NOT_RUN: 0,
    PENDING: 0,
  };
  for (const receipt of receipts) {
    const status = resolveStoredVerdict({
      verdict: receipt.payload as Parameters<typeof resolveStoredVerdict>[0]["verdict"],
      now,
    }).status;
    counts[status as GateRecordStatus] += 1;
  }
  return counts;
}

/**
 * MISMATCH belongs in the non-passing total. Omitted, an accusation would leave
 * the headline "nothing to report" number untouched. PENDING belongs for the
 * same reason pointed the other way: it is emphatically NOT a pass, and leaving
 * it out would make a workspace whose checks are all still inside their windows
 * report the same headline as one where everything passed.
 */
export function countNonPassingReceipts(counts: Record<GateRecordStatus, number>): number {
  return counts.MISMATCH + counts.FAIL + counts.UNKNOWN + counts.NOT_RUN + counts.PENDING;
}

/** The sentence a reader of this screen actually gets. Never returns "" for any input. */
export function describeCheckResult(result: CheckResult): string {
  const tail = " This result is not a signed receipt.";
  if (result.counts) {
    return `${result.label} finished with ${result.counts.passed} passing and ${result.counts.failed} failing gates.${tail}`;
  }
  if (!result.verdict) {
    return `${result.label} returned a durable result record with no verdict, so Excellent cannot say what the check found. Open the retained record before treating this as an outcome.${tail}`;
  }
  return `${result.label} ${VERDICT_SENTENCE[result.verdict]}. No per-gate tally was returned.${tail}`;
}

function CandidateCheckProgress({
  candidate,
  review,
  onNavigate,
  requireDurableAction,
  accountReady,
  accountUnavailable,
  sourceLabel,
}: {
  candidate: ObservationCandidate;
  review: ObservationReview;
  onNavigate: (to: string) => void;
  requireDurableAction: (run: () => void | Promise<void>) => void;
  accountReady: boolean;
  accountUnavailable: boolean;
  sourceLabel: string | null;
}) {
  const gatePreview = useApi(gates.list, {});
  const workflowActions = useWorkflowActions("real");
  const [selectedProjectAppId, setSelectedProjectAppId] = useState<number | null>(() =>
    readFirstUnknownSelectedProject(review.id),
  );
  const [runningCheck, setRunningCheck] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [confirmingFirstUnknown, setConfirmingFirstUnknown] = useState(false);
  const [confirmedContract, setConfirmedContract] =
    useState<ObservationFirstUnknownReview["acceptedContract"]>(null);
  const [acceptedFirstUnknownReview, setAcceptedFirstUnknownReview] =
    useState<ObservationFirstUnknownReview | null>(null);
  const [acceptedOracleContext, setAcceptedOracleContext] = useState<{
    project: ProjectGatesWire;
    check: GateDefWire;
  } | null>(null);
  const [savedWorkflowId, setSavedWorkflowId] = useState<string | null>(null);
  const [savedWorkflowAdmitted, setSavedWorkflowAdmitted] = useState<boolean | null>(null);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [executionUncertain, setExecutionUncertain] = useState(false);
  const [uncertainBaselineIds, setUncertainBaselineIds] = useState<number[]>([]);
  const [proofHref, setProofHref] = useState<string | null>(null);
  const [workflowMutationUncertain, setWorkflowMutationUncertain] = useState<
    "create" | "admit" | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const projectsWithChecks =
    gatePreview.data?.projects.filter((project) => project.gates.length > 0) ?? [];
  const durableWorkflow =
    workflowActions.workflows.find((workflow) =>
      workflow.evidence.includes(`Observation review: ${review.id}`),
    ) ?? null;
  const candidateSupportsProjectCheck = candidate.id === "repository-change";
  const durableProject = durableWorkflow?.oracle
    ? (projectsWithChecks.find((project) =>
        durableWorkflow.oracle?.startsWith(`${project.root}: `),
      ) ?? null)
    : null;
  const proposedProject = candidateSupportsProjectCheck
    ? (durableProject ??
      projectsWithChecks.find((project) => project.appId === selectedProjectAppId) ??
      null)
    : null;
  const durableOracleCheck =
    durableWorkflow?.oracle && durableProject
      ? (durableProject.gates.find(
          (gate) => durableWorkflow.oracle === `${durableProject.root}: ${gate.command}`,
        ) ?? null)
      : null;
  const proposedCheck = proposedProject
    ? durableWorkflow
      ? durableOracleCheck
      : (["check-contract-seams", "typecheck", "lint", "test", "build"]
          .map((id) => proposedProject.gates.find((gate) => gate.id === id))
          .find(Boolean) ??
        proposedProject.gates[0] ??
        null)
    : null;
  const actionableProject = acceptedOracleContext?.project ?? proposedProject;
  const actionableCheck = acceptedOracleContext?.check ?? proposedCheck;
  const firstUnknownProposal = useApi(
    verificationObservation.firstUnknownReview,
    accountReady
      ? {
          params: { id: review.id },
          query:
            proposedProject && proposedCheck
              ? {
                  projectName: proposedProject.name,
                  projectRoot: proposedProject.root,
                  gateId: proposedCheck.id,
                  command: proposedCheck.command,
                }
              : undefined,
        }
      : null,
  );
  const staleDurableOracle = Boolean(durableWorkflow && (!durableProject || !durableOracleCheck));
  const effectiveSavedWorkflowId = savedWorkflowId ?? durableWorkflow?.id ?? null;
  const effectiveWorkflowAdmitted = savedWorkflowId
    ? savedWorkflowAdmitted === true
    : durableWorkflow?.status === "admitted";
  const gateActivity = useApi(
    activity.list,
    effectiveSavedWorkflowId ? { query: { env: "real", category: "checks" } } : null,
  );
  const durableCheckEvent = effectiveSavedWorkflowId
    ? (gateActivity.data?.groups
        .flatMap((group) => group.members)
        .find((member) => isWorkflowResultHref(member.href, effectiveSavedWorkflowId)) ?? null)
    : null;
  const hasCompletedCheck = Boolean(checkResult || durableCheckEvent);
  const workflowLookupPending =
    workflowActions.ready &&
    workflowActions.workflowsApi.isLoading &&
    !workflowActions.workflowsApi.data;
  const workflowLookupFailed =
    !workflowActions.ready || Boolean(workflowActions.workflowsApi.error);
  const activityLookupPending = Boolean(
    effectiveSavedWorkflowId && gateActivity.isLoading && !gateActivity.data,
  );
  const activityLookupFailed = Boolean(effectiveSavedWorkflowId && gateActivity.error);
  const exactProofHref = proofHref ?? durableCheckEvent?.href ?? null;
  const firstUnknown = acceptedFirstUnknownReview ?? firstUnknownProposal.data?.review ?? null;
  const firstUnknownAcceptedContract = confirmedContract ?? firstUnknown?.acceptedContract ?? null;
  const selectedFirstUnknownCardIds = firstUnknown?.selectedCardIds ?? [];
  const firstUnknownHasGaps =
    firstUnknown?.cards.some(
      (card) => card.gaps.length > 0 || card.admissionStatus !== "admitted",
    ) ?? false;
  const firstUnknownLoading = Boolean(
    accountReady && firstUnknownProposal.isLoading && !firstUnknownProposal.data,
  );
  const firstUnknownFailed = Boolean(accountReady && firstUnknownProposal.error);
  const progressTitle = !accountReady
    ? accountUnavailable
      ? "Account status is unavailable."
      : "Checking account status."
    : !firstUnknownAcceptedContract
      ? firstUnknownLoading
        ? "Preparing the canonical proposal."
        : firstUnknownFailed
          ? "Canonical proposal is temporarily unavailable."
          : firstUnknownHasGaps
            ? "This proposal has explicit gaps."
            : firstUnknown && proposedCheck
              ? "Review the first-UNKNOWN guarantee."
              : firstUnknown
                ? "The first-UNKNOWN proposal needs an oracle."
                : "Preparing the canonical proposal."
      : checkResult || durableCheckEvent
        ? "The named check has finished."
        : executionUncertain
          ? "The check outcome needs reconciliation."
          : workflowMutationUncertain
            ? "The guarantee update needs reconciliation."
            : workflowLookupPending
              ? "Restoring the saved guarantee."
              : workflowLookupFailed
                ? "Saved guarantees are temporarily unavailable."
                : gatePreview.isLoading
                  ? "Looking for local project checks."
                  : gatePreview.error
                    ? "Existing checks are temporarily unavailable."
                    : staleDurableOracle
                      ? "The saved oracle is no longer available."
                      : effectiveSavedWorkflowId && !effectiveWorkflowAdmitted
                        ? "The guarantee is saved as a draft."
                        : activityLookupPending
                          ? "Checking for an earlier result."
                          : activityLookupFailed
                            ? "Earlier check results are temporarily unavailable."
                            : proposedCheck
                              ? `${proposedCheck.label} is ready to confirm.`
                              : candidateSupportsProjectCheck && projectsWithChecks.length > 0
                                ? "Choose the project this pattern belongs to."
                                : "This pattern still needs an independent oracle.";
  const progressDescription = !accountReady
    ? accountUnavailable
      ? "Excellent cannot expose a retained workflow or check action until account status is confirmed."
      : "Excellent is confirming the account boundary before showing a retained workflow or check action."
    : !firstUnknownAcceptedContract
      ? firstUnknownLoading
        ? "Excellent is creating a durable candidate through the workflow-independent first-UNKNOWN path. Nothing is being accepted or run yet."
        : firstUnknownFailed
          ? "Excellent cannot safely load the canonical first-UNKNOWN proposal. Retry before confirming anything."
          : firstUnknownHasGaps
            ? "Unsupported or ungroundable criteria remain explicit gaps or require authorized human judgment. This cannot be accepted as a production guarantee."
            : firstUnknown && proposedProject && proposedCheck
              ? `The historical occurrence stays UNKNOWN. If accepted, the contract applies only to the next occurrence and ${proposedCheck.command} can be used as the independent check.`
              : firstUnknown
                ? "The historical occurrence stays UNKNOWN. Choose a project/check so the next occurrence has an independent falsifier."
                : "Excellent is preparing the active first-UNKNOWN proposal from the retained observation review."
      : checkResult
        ? describeCheckResult(checkResult)
        : durableCheckEvent
          ? "A workflow-linked result record is retained in Activity. Open it to inspect the candidate, project, named check, outcome, and evidence limitations."
          : executionUncertain
            ? "The request did not return a trustworthy outcome. Excellent will not offer the check again until the linked Activity record is reconciled."
            : workflowMutationUncertain
              ? "The request did not return a trustworthy workflow state. Excellent will not create, admit, or run anything else until the saved workflow list is reconciled."
              : workflowLookupPending
                ? "Excellent is reading only the local workflow record linked to this saved review. Nothing is being created or run."
                : workflowLookupFailed
                  ? "Excellent cannot safely decide whether a guarantee already exists, so creating another one is blocked."
                  : gatePreview.isLoading
                    ? "Excellent is reading local project check definitions. Nothing is being created or run."
                    : gatePreview.error
                      ? "Excellent cannot bind this candidate to a named local check until check lookup succeeds."
                      : staleDurableOracle
                        ? "The project or exact command bound to this durable workflow is missing. Excellent will not substitute another project or check."
                        : effectiveSavedWorkflowId && !effectiveWorkflowAdmitted
                          ? "The durable workflow exists, but it is not admitted and cannot run. Finishing admission does not run the check."
                          : activityLookupPending
                            ? "Excellent is checking local Activity for a result linked to this workflow before offering another run."
                            : activityLookupFailed
                              ? "Excellent cannot safely determine whether this workflow already has a result, so another run is blocked."
                              : effectiveSavedWorkflowId && proposedProject && proposedCheck
                                ? `The future guarantee is bound to ${proposedProject.name}, with ${proposedCheck.command} as its named oracle. The check has not run.`
                                : proposedProject && proposedCheck
                                  ? `For future repository changes, ${proposedProject.name} can run ${proposedCheck.command}. Confirm only if this project and check can falsify the guarantee.`
                                  : candidateSupportsProjectCheck && projectsWithChecks.length > 0
                                    ? "The aggregate observation did not reveal a repository path. Choose the project context before Excellent proposes a check."
                                    : `“${candidate.label}” is durably reviewed, but aggregate counts do not identify an independent oracle. It remains a candidate until one is supplied.`;

  async function confirmFirstUnknownContract() {
    if (!firstUnknown || selectedFirstUnknownCardIds.length === 0) return;
    const acceptedOracle =
      proposedProject && proposedCheck ? { project: proposedProject, check: proposedCheck } : null;
    setConfirmingFirstUnknown(true);
    setActionError(null);
    try {
      const result = await apiCall(verificationObservation.confirmFirstUnknownReview, {
        params: { id: review.id },
        body: {
          expectedCandidateDigest: firstUnknown.candidateDigest,
          selectedCardIds: selectedFirstUnknownCardIds,
          idempotencyKey: `first-unknown:${review.id}:${firstUnknown.candidateDigest}`,
          oracleContext:
            proposedProject && proposedCheck
              ? {
                  projectName: proposedProject.name,
                  projectRoot: proposedProject.root,
                  gateId: proposedCheck.id,
                  command: proposedCheck.command,
                }
              : null,
        },
      });
      if (result.status === "rejected") {
        const code = result.error.code;
        setActionError(
          code === "candidate-stale"
            ? "This proposal changed or is unavailable. Reload the proposal before confirming."
            : code === "policy-authority-required"
              ? "A required protected card cannot be removed without policy authority."
              : code === "admission-gap"
                ? "This candidate cannot be accepted yet because an evaluator, oracle, predicate, evidence kind, or work type is unsupported."
                : code === "idempotency-conflict"
                  ? "This confirmation key was already used for a different selection. Reload the proposal before continuing."
                  : "This candidate cannot be accepted from the current review. Reload the proposal before continuing.",
        );
        return;
      }
      setConfirmedContract(result.acceptedContract);
      setAcceptedFirstUnknownReview(firstUnknown);
      setAcceptedOracleContext(acceptedOracle);
    } catch (error) {
      setActionError(
        `${errorMessage(error)} Excellent could not confirm whether the contract was accepted. Reconcile the proposal before continuing.`,
      );
    } finally {
      setConfirmingFirstUnknown(false);
    }
  }

  async function saveWorkflowProposal() {
    if (!proposedProject || !proposedCheck) return;
    setSavingWorkflow(true);
    setActionError(null);
    setWorkflowMutationUncertain(null);
    try {
      const oracle = `${proposedProject.root}: ${proposedCheck.command}`;
      const created = await workflowActions.createWorkflow({
        name: candidate.label,
        actor: "local-aggregate-observation",
        oracle,
        doc: `${candidate.label}. ${candidate.basis}`,
        evidence: `${candidate.basis} Observation review: ${review.id}. Project context confirmed: ${proposedProject.name} (${proposedProject.root}). Proposed independent check: ${oracle}`,
      });
      if (!created.ok || !created.createdId) {
        setWorkflowMutationUncertain("create");
        setActionError(
          "Excellent could not confirm whether the guarantee was saved. No check is available until the workflow list is reconciled.",
        );
        return;
      }
      setSavedWorkflowId(created.createdId);
      if (created.workflow.status !== "admitted") {
        const admitted = await workflowActions.admitWorkflow(created.createdId);
        if (!admitted.ok) {
          setSavedWorkflowAdmitted(false);
          setWorkflowMutationUncertain("admit");
          setActionError(
            "The guarantee exists, but Excellent could not confirm whether admission completed. No check is available until the workflow list is reconciled.",
          );
          return;
        }
      }
      setSavedWorkflowAdmitted(true);
    } finally {
      setSavingWorkflow(false);
    }
  }

  async function finishWorkflowAdmission() {
    if (!effectiveSavedWorkflowId) return;
    setSavingWorkflow(true);
    setActionError(null);
    setWorkflowMutationUncertain(null);
    try {
      const admitted = await workflowActions.admitWorkflow(effectiveSavedWorkflowId);
      if (!admitted.ok) {
        setWorkflowMutationUncertain("admit");
        setActionError(
          "Excellent could not confirm whether admission completed. No check is available until the workflow list is reconciled.",
        );
        return;
      }
      setSavedWorkflowAdmitted(true);
    } finally {
      setSavingWorkflow(false);
    }
  }

  async function reconcileWorkflowMutation() {
    setActionError(null);
    try {
      const refreshed = await workflowActions.workflowsApi.mutate();
      const linked =
        refreshed?.items.find((workflow) =>
          workflow.evidence.includes(`Observation review: ${review.id}`),
        ) ?? null;
      if (linked) {
        setSavedWorkflowId(linked.id);
        setSavedWorkflowAdmitted(linked.status === "admitted");
        setWorkflowMutationUncertain(null);
        return;
      }
      if (workflowMutationUncertain === "create") {
        setWorkflowMutationUncertain(null);
        setActionError("No linked guarantee was found. You can confirm the save again.");
        return;
      }
      setActionError(
        "The known guarantee is not visible in workflow history. No further action is available yet.",
      );
    } catch {
      setActionError(
        "Workflow history is unavailable, so the guarantee update cannot be reconciled yet.",
      );
    }
  }

  async function confirmAndRunCheck() {
    if (!firstUnknownAcceptedContract || !actionableProject || !actionableCheck) return;
    setRunningCheck(true);
    setActionError(null);
    setExecutionUncertain(false);
    setUncertainBaselineIds(
      gateActivity.data?.groups.flatMap((group) => group.members).map((member) => member.id) ?? [],
    );
    try {
      const result = await apiCall(gates.run, {
        body: {
          appId: actionableProject.appId,
          gateId: actionableCheck.id,
          observationReviewId: review.id,
          acceptedContractDigest: firstUnknownAcceptedContract.contractDigest,
        },
      });
      const summary = result.project.lastRun;
      if (result.resultRecord) {
        setProofHref(result.resultRecord.href);
        setExecutionUncertain(false);
        if (!summary) {
          // The record's verdict is carried, not flattened into a two-value tally. The
          // pair of ternaries that used to stand here sent MISMATCH, UNKNOWN, NOT_RUN and
          // a missing status alike to 0/0 — "0 passing and 0 failing gates".
          setCheckResult(projectCheckResult(actionableCheck.label, result.resultRecord.status));
        }
      }
      if (summary) {
        setCheckResult({
          label: actionableCheck.label,
          counts: { passed: summary.passed, failed: summary.failed },
          verdict: undefined,
        });
      } else {
        setExecutionUncertain(true);
        setActionError(
          "The request returned without a check summary. Confirm the outcome in Activity before trying again.",
        );
      }
      if (!result.resultRecord && summary) {
        setActionError("The check finished, but no durable result record was returned.");
      }
      await Promise.allSettled([gatePreview.mutate(), gateActivity.mutate()]);
    } catch (error) {
      setExecutionUncertain(true);
      setActionError(
        `${errorMessage(error)} Excellent could not confirm whether the check ran. It will not offer another run until Activity is reconciled.`,
      );
    } finally {
      setRunningCheck(false);
    }
  }

  async function refreshProofRecord() {
    setActionError(null);
    try {
      const refreshed = await gateActivity.mutate();
      const match = effectiveSavedWorkflowId
        ? refreshed?.groups
            .flatMap((group) => group.members)
            .find(
              (member) =>
                isWorkflowResultHref(member.href, effectiveSavedWorkflowId) &&
                (!executionUncertain || !uncertainBaselineIds.includes(member.id)),
            )
        : null;
      if (match?.href) {
        setProofHref(match.href);
        setExecutionUncertain(false);
        return;
      }
      setActionError(
        "No linked result record is visible yet. The check will not be offered again from this state.",
      );
    } catch {
      setActionError("Activity is unavailable, so the check outcome cannot be reconciled yet.");
    }
  }

  return (
    <section className="vp-home-section vp-check-preview" aria-labelledby="detected-checks-title">
      <div>
        <span>Independent checks</span>
        <h2 id="detected-checks-title">{progressTitle}</h2>
        <p>{progressDescription}</p>
        <details className="vp-home-more">
          <summary>Why this candidate?</summary>
          <dl className="vp-observation-scope">
            <div>
              <dt>Candidate</dt>
              <dd>{candidate.label}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>
                {sourceLabel
                  ? `${sourceLabel} aggregate session events`
                  : "Source not retained in this review record"}
                , reviewed {formatTime(review.reviewedAt)}
              </dd>
            </div>
            <div>
              <dt>Basis</dt>
              <dd>{candidate.basis}</dd>
            </div>
            <div>
              <dt>Limit</dt>
              <dd>Aggregate history does not prove an outcome or identify project context.</dd>
            </div>
          </dl>
        </details>
        {firstUnknown ? (
          <div className="vp-first-unknown-review" aria-label="First UNKNOWN proposal">
            <small>
              {firstUnknown.domainExtension}@{firstUnknown.extensionVersion} · candidate{" "}
              {firstUnknown.candidateDigest.slice(0, 19)}
            </small>
            {firstUnknown.cards.map((card) => (
              <article key={card.id} className="vp-first-unknown-card">
                <h3>{card.willCheck}</h3>
                <p>{card.why}</p>
                <dl className="vp-observation-scope">
                  <div>
                    <dt>Status</dt>
                    <dd>{card.admissionStatus === "admitted" ? "Admitted" : "Gap"}</dd>
                  </div>
                  <div>
                    <dt>Falsifier</dt>
                    <dd>{card.falsifiers.join(" ")}</dd>
                  </div>
                  <div>
                    <dt>Evidence</dt>
                    <dd>{card.evidenceOracles.join(", ")}</dd>
                  </div>
                </dl>
                {card.gaps.length > 0 ? (
                  <ul className="vp-observation-gaps">
                    {card.gaps.map((gap) => (
                      <li key={gap}>{gap}</li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
            {firstUnknownAcceptedContract ? (
              <p className="vp-observation-success">
                Contract accepted for the next occurrence:{" "}
                {firstUnknownAcceptedContract.contractDigest.slice(0, 19)}. The first occurrence
                remains UNKNOWN.
              </p>
            ) : null}
          </div>
        ) : null}
        {accountReady &&
        !firstUnknownAcceptedContract &&
        !workflowLookupPending &&
        !workflowLookupFailed &&
        !workflowMutationUncertain &&
        !gatePreview.isLoading &&
        !gatePreview.error &&
        !activityLookupPending &&
        !activityLookupFailed &&
        !executionUncertain &&
        !staleDurableOracle &&
        candidateSupportsProjectCheck &&
        !durableWorkflow &&
        !proposedProject &&
        projectsWithChecks.length > 0 ? (
          <div className="vp-project-choices" aria-label="Project context">
            {projectsWithChecks.map((project) => (
              <button
                type="button"
                key={project.appId}
                data-action-id={HOME_ACTION.chooseProject}
                aria-pressed="false"
                onClick={() => {
                  setSelectedProjectAppId(project.appId);
                  writeFirstUnknownSelectedProject(review.id, project.appId);
                  setSavedWorkflowId(null);
                  setSavedWorkflowAdmitted(null);
                  setCheckResult(null);
                  setConfirmedContract(null);
                  setAcceptedFirstUnknownReview(null);
                  setAcceptedOracleContext(null);
                }}
              >
                <strong>{project.name}</strong>
                <small>
                  {project.gates.length} available check{project.gates.length === 1 ? "" : "s"}
                </small>
              </button>
            ))}
          </div>
        ) : null}
        {actionError ? (
          <p className="vp-observation-error" role="alert">
            {actionError}
          </p>
        ) : null}
      </div>
      {!accountReady ? (
        accountUnavailable ? (
          <button type="button" data-action-id={HOME_ACTION.resolveAccount} onClick={() => requireDurableAction(() => undefined)}>
            Resolve account status
          </button>
        ) : null
      ) : !firstUnknownAcceptedContract ? (
        firstUnknownFailed ? (
          <button type="button" data-action-id={HOME_ACTION.reloadProposal} onClick={() => void firstUnknownProposal.mutate()}>
            Reload proposal
          </button>
        ) : firstUnknownHasGaps ? (
          <button type="button" data-action-id={HOME_ACTION.reviewChecks} onClick={() => onNavigate("/workflows")}>
            Review checks
          </button>
        ) : firstUnknownLoading ? null : firstUnknown && proposedProject && proposedCheck ? (
          <button
            type="button"
            disabled={confirmingFirstUnknown}
            onClick={() => requireDurableAction(confirmFirstUnknownContract)}
          >
            {confirmingFirstUnknown ? "Accepting contract" : "Confirm accepted contract"}
          </button>
        ) : !gatePreview.isLoading &&
          (!candidateSupportsProjectCheck || projectsWithChecks.length === 0) ? (
          <button type="button" data-action-id={HOME_ACTION.reviewChecks} onClick={() => onNavigate("/workflows")}>
            Review checks
          </button>
        ) : null
      ) : hasCompletedCheck && exactProofHref ? (
        <button type="button" data-action-id={HOME_ACTION.openProofRecord} onClick={() => onNavigate(exactProofHref)}>
          Open proof record
        </button>
      ) : hasCompletedCheck || executionUncertain ? (
        <button
          type="button"
          disabled={gateActivity.isLoading}
          onClick={() => void refreshProofRecord()}
        >
          {gateActivity.isLoading ? "Checking Activity" : "Refresh proof record"}
        </button>
      ) : workflowMutationUncertain ? (
        <button type="button" data-action-id={HOME_ACTION.reconcileGuarantee} onClick={() => void reconcileWorkflowMutation()}>
          Reconcile guarantee
        </button>
      ) : workflowLookupFailed ? (
        <button type="button" data-action-id={HOME_ACTION.retryGuaranteeLookup} onClick={() => void workflowActions.workflowsApi.mutate()}>
          Retry guarantee lookup
        </button>
      ) : gatePreview.error ? (
        <button type="button" data-action-id={HOME_ACTION.retryCheckLookup} onClick={() => void gatePreview.mutate()}>
          Retry check lookup
        </button>
      ) : staleDurableOracle && effectiveSavedWorkflowId ? (
        <button
          type="button"
          data-action-id={HOME_ACTION.openSavedGuarantee}
          onClick={() => onNavigate(`/workflows/${encodeURIComponent(effectiveSavedWorkflowId)}`)}
        >
          Open saved guarantee
        </button>
      ) : activityLookupFailed ? (
        <button type="button" data-action-id={HOME_ACTION.retryResultLookup} onClick={() => void refreshProofRecord()}>
          Retry result lookup
        </button>
      ) : workflowLookupPending ||
        gatePreview.isLoading ||
        activityLookupPending ? null : effectiveSavedWorkflowId && !effectiveWorkflowAdmitted ? (
        <button type="button" data-action-id={HOME_ACTION.openLegacyCheckSetup} onClick={() => onNavigate("/workflows")}>
          Open legacy check setup
        </button>
      ) : firstUnknownAcceptedContract && actionableProject && actionableCheck ? (
        <button
          type="button"
          disabled={runningCheck}
          onClick={() => requireDurableAction(confirmAndRunCheck)}
        >
          {runningCheck
            ? `Running ${actionableCheck.label}`
            : `Run independent check: ${actionableCheck.label}`}
        </button>
      ) : proposedProject && proposedCheck ? (
        <button type="button" data-action-id={HOME_ACTION.prepareNextRunCheck} onClick={() => onNavigate("/workflows")}>
          Prepare next-run check
        </button>
      ) : !gatePreview.isLoading &&
        (!candidateSupportsProjectCheck || projectsWithChecks.length === 0) ? (
        <button type="button" data-action-id={HOME_ACTION.reviewChecks} onClick={() => onNavigate("/workflows")}>
          Review checks
        </button>
      ) : null}
    </section>
  );
}

function formatList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

function firstUnknownSelectedProjectKey(reviewId: string): string {
  return `excellent:first-unknown:selected-project:${reviewId}`;
}

function readFirstUnknownSelectedProject(reviewId: string): number | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(firstUnknownSelectedProjectKey(reviewId));
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function writeFirstUnknownSelectedProject(reviewId: string, appId: number): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(firstUnknownSelectedProjectKey(reviewId), String(appId));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "an unknown time";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function isWorkflowResultHref(href: string, workflowId: string): boolean {
  const [pathname, search = ""] = href.split("?");
  if (pathname !== `/workflows/${encodeURIComponent(workflowId)}`) return false;
  return /^\d+$/.test(new URLSearchParams(search).get("result") ?? "");
}
